import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  const from = (location.state as any)?.from?.pathname || '/';

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      await login(data.email, data.password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error: any) {
      toast.error(error.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sage-600">
              <svg viewBox="0 0 24 24" className="h-6 w-6 text-white" fill="currentColor">
                <circle cx="12" cy="6" r="3" />
                <path d="M12 9v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <span className="text-xl font-semibold text-sand-900">GolfBook</span>
          </Link>
          <h1 className="mt-6 text-2xl font-bold text-sand-900">Welcome back</h1>
          <p className="mt-2 text-sand-500">Sign in to your account to continue</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Input
              {...register('email')}
              type="email"
              label="Email"
              placeholder="you@example.com"
              error={errors.email?.message}
            />

            <div>
              <Input
                {...register('password')}
                type="password"
                label="Password"
                placeholder="Enter your password"
                error={errors.password?.message}
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-sm text-sage-600 hover:text-sage-700">
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button type="submit" variant="sage" className="w-full" isLoading={isLoading}>
              Sign In
            </Button>
          </form>
        </Card>

        <p className="mt-6 text-center text-sm text-sand-500">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="font-medium text-sage-600 hover:text-sage-700">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
